/**
 * GuestInvitePanel + DetailsAccordion + StatRow + timeAgo helper.
 * Phase 7 extract from pages/Workspace.jsx.
 */
import React from "react";
import api from "@/lib/api";
import friendlyApiError from "@/lib/apiErrors";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import { toast } from "sonner";

// Guest-collaborator panel: invite / list / revoke external users.
export function GuestInvitePanel({ workspace }) {
    const [guests, setGuests] = React.useState([]);
    const [email, setEmail] = React.useState("");
    const [access, setAccess] = React.useState("R");
    const [busy, setBusy] = React.useState(false);
    // Preview state — sender sees exact email body before it goes out.
    const [preview, setPreview] = React.useState(null);
    const [previewBusy, setPreviewBusy] = React.useState(false);

    const refresh = React.useCallback(async () => {
        try {
            const r = await api.get(`/workspaces/${workspace.id}/members`);
            setGuests(r.data?.guests || []);
        } catch { /* noop */ }
    }, [workspace.id]);
    React.useEffect(() => { refresh(); }, [refresh]);

    const openPreview = async (e) => {
        e?.preventDefault?.();
        if (!email.trim()) return;
        setPreviewBusy(true);
        try {
            const r = await api.post(`/workspaces/${workspace.id}/guest-invite/preview`,
                { email: email.trim().toLowerCase(), access });
            setPreview(r.data);
        } catch (err) {
            toast.error(friendlyApiError(err, "Could not render preview"));
        } finally { setPreviewBusy(false); }
    };

    const confirmSend = async () => {
        if (!preview) return;
        setBusy(true);
        try {
            const r = await api.post(`/workspaces/${workspace.id}/guest-invite`,
                { email: preview.to_email, access: preview.access });
            const d = r.data || {};
            if (d.email_sent) {
                toast.success(`Invitation email sent to ${preview.to_email}`);
            } else if (d.email_error) {
                toast.warning(`Guest added — email delivery failed: ${d.email_error}. Configure SMTP in Settings → SMTP.`);
            } else {
                toast.success(`Invited ${preview.to_email} (SMTP not configured, no email sent)`);
            }
            setEmail(""); setAccess("R"); setPreview(null);
            refresh();
        } catch (err) {
            toast.error(friendlyApiError(err, "Invite failed"));
        } finally { setBusy(false); }
    };

    const revoke = async (shareId) => {
        try {
            await api.delete(`/workspaces/${workspace.id}/guests/${shareId}`);
            toast.success("Access revoked");
            refresh();
        } catch (err) {
            toast.error(friendlyApiError(err, "Revoke failed"));
        }
    };

    const updateAccess = async (shareId, newAccess) => {
        try {
            await api.patch(`/workspaces/${workspace.id}/guests/${shareId}`, { access: newAccess });
            toast.success("Access updated");
            refresh();
        } catch (err) {
            toast.error(friendlyApiError(err, "Update failed"));
        }
    };

    return (
        <div className="space-y-2" data-testid="guest-invite-panel">
            <div className="flex items-center justify-between">
                <div className="eyebrow text-[10px]">Invite external guest</div>
                <Badge variant="outline" className="font-mono text-[9px] uppercase">Cross-entity</Badge>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug font-mono">
                Share this workspace with collaborators from other organisations. They'll receive an email invite and need to accept before they see anything.
            </p>
            <form onSubmit={openPreview} className="flex gap-1.5 items-center">
                <input type="email" required placeholder="guest@another-org.com"
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       className="flex-1 h-8 px-2 text-xs bg-background border border-border rounded-sm"
                       data-testid="guest-invite-email" />
                <select value={access} onChange={(e) => setAccess(e.target.value)}
                        className="h-8 px-2 text-xs bg-background border border-border rounded-sm font-mono"
                        data-testid="guest-invite-access">
                    <option value="R">Read</option>
                    <option value="W">Write</option>
                    <option value="A">Admin</option>
                </select>
                <Button size="sm" type="submit" disabled={previewBusy} className="h-8 text-xs" data-testid="guest-invite-submit">
                    {previewBusy ? "Loading…" : "Preview & invite"}
                </Button>
            </form>
            {/* Email preview modal — sender reviews exact subject + body before sending. */}
            <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="guest-invite-preview-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-base">Preview invitation email</DialogTitle>
                        <DialogDescription className="text-xs">
                            This is exactly what <span className="font-mono">{preview?.to_email}</span> will receive.
                            You can customise the template in <b>Settings → Email Templates</b>.
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="space-y-3 overflow-y-auto">
                            <div className="grid grid-cols-[90px_1fr] gap-2 text-xs font-mono border border-border rounded-sm p-3 bg-secondary/20">
                                <span className="text-muted-foreground">To:</span><span data-testid="preview-to">{preview.to_email}</span>
                                <span className="text-muted-foreground">Access:</span><span data-testid="preview-access">{preview.access}</span>
                                <span className="text-muted-foreground">Subject:</span><span className="font-semibold" data-testid="preview-subject">{preview.subject}</span>
                            </div>
                            {!preview.smtp_configured && (
                                <div className="text-[11px] border border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-sm px-3 py-2" data-testid="preview-smtp-warning">
                                    SMTP is not configured for this tenant. The share will still be created but the email
                                    won't be delivered. Configure it under <b>Settings → SMTP</b>.
                                </div>
                            )}
                            <div className="border border-border rounded-sm overflow-hidden">
                                <iframe title="invite-preview" srcDoc={preview.html} className="w-full" style={{ height: 420, background: "#f5f4f0", border: 0 }} sandbox="" data-testid="preview-html-iframe" />
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2 border-t border-border">
                        <Button variant="outline" size="sm" onClick={() => setPreview(null)} data-testid="preview-cancel">Cancel</Button>
                        <Button size="sm" onClick={confirmSend} disabled={busy} data-testid="preview-confirm-send">
                            {busy ? "Sending…" : "Send invitation"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            {guests.length > 0 && (
                <div className="space-y-1 pt-2 border-t border-border">
                    <div className="eyebrow text-[10px] pt-1 pb-0.5">Current guests ({guests.length})</div>
                    {guests.map((g) => (
                        <div key={g.share_id} className="flex items-center gap-2 text-xs bg-secondary/20 px-2 py-1.5 rounded-sm" data-testid={`guest-row-${g.share_id}`}>
                            <span className="flex-1 truncate font-mono" title={g.email}>{g.email}</span>
                            {g.invite_url && (
                                <button type="button"
                                    onClick={() => {
                                        navigator.clipboard?.writeText(g.invite_url).then(
                                            () => toast.success("Invite link copied"),
                                            () => toast.error("Copy failed"),
                                        );
                                    }}
                                    className="text-[9px] font-mono uppercase px-1.5 py-0.5 border border-border rounded-sm hover:border-primary hover:text-primary"
                                    data-testid={`guest-copy-link-${g.share_id}`}
                                    title="Copy magic accept-invite URL">
                                    Copy link
                                </button>
                            )}
                            <select value={g.access} onChange={(e) => updateAccess(g.share_id, e.target.value)}
                                    className="h-6 px-1 text-[10px] bg-transparent border border-border rounded-sm font-mono"
                                    data-testid={`guest-access-${g.share_id}`}>
                                <option value="R">R</option><option value="W">W</option><option value="A">A</option>
                            </select>
                            <button onClick={() => revoke(g.share_id)} className="text-muted-foreground hover:text-destructive" data-testid={`guest-revoke-${g.share_id}`}>
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {guests.length === 0 && (
                <div className="text-[11px] text-muted-foreground font-mono">No external guests yet.</div>
            )}
        </div>
    );
}

// Compact two-cell row used in the "Quick stats" strip at the top of the
// details aside. Keeps the formerly cluttered single-column stack readable
// without sacrificing any data.
export function StatRow({ label, value, testId, title }) {
    return (
        <div className="flex flex-col min-w-0" title={title} data-testid={testId}>
            <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/80">{label}</span>
            <span className="text-[12px] font-medium truncate">{value || "—"}</span>
        </div>
    );
}

// Collapsible "Show more" disclosure for rarely-needed metadata so it's not
// constantly competing with actionable items (Sharing, External guests).
export function DetailsAccordion({ selected, createdByName, userById }) {
    const [open, setOpen] = React.useState(false);
    const createdAtLabel = selected?.created_at ? formatDate(selected.created_at) : "—";
    return (
        <div className="border-t border-border pt-3" data-testid="workspace-details-accordion">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                data-testid="workspace-details-accordion-toggle">
                <span>{open ? "Hide" : "Show"} more details</span>
                <span className={`text-[10px] transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
            </button>
            {open && (
                <div className="space-y-3 pt-3" data-testid="workspace-details-accordion-content">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Name</div>
                        <div className="text-sm font-medium">{selected.name}</div>
                    </div>
                    {selected.tenant_name && (
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Entity</div>
                            <div className="text-sm" data-testid="workspace-tenant-name">{selected.org_name || selected.tenant_name}</div>
                        </div>
                    )}
                     {(!selected.virtual || selected.created_at) && (
                        <>
                            {!selected.virtual && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Created by</div>
                                    <div className="flex items-center gap-2" data-testid="workspace-created-by">
                                        <div className="w-6 h-6 bg-foreground text-background font-mono text-[10px] font-semibold flex items-center justify-center rounded-sm shrink-0">
                                            {createdByName(selected).split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm truncate">{createdByName(selected)}</div>
                                            {userById(selected.created_by) && (
                                                <div className="text-[10px] font-mono text-muted-foreground truncate">{userById(selected.created_by).email}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                        )}
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Created on</div>
                                <div className="text-xs font-mono text-muted-foreground">{createdAtLabel}</div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// Friendly relative time for the Quick stats strip — "2h ago", "3d ago",
// etc. Falls back to a locale string for older dates so users still get
// precise info on hover via the title attribute.
export function timeAgo(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    if (day < 30) return `${Math.floor(day / 7)}w ago`;
    return d.toLocaleDateString();
}

export function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const diffMs = Date.now() - d.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;


}